import { compact } from './plotlyTheme';

/**
 * Turns the hast node of a GFM markdown table into structured data.
 *
 * The chat renders model-written markdown, so a table arrives as text. To sort,
 * copy and export it we need the values, not just rendered children — hence
 * reading the node rather than reconciling React elements.
 *
 * Cell content is flattened to text. A data table from the model is names and
 * figures; a cell carrying a link or an image would lose it, which is the trade
 * for getting a column that sorts numerically.
 */

const ARABIC_RE =
  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

const TOTALS_RE =
  /^\s*(الإجمالي|الاجمالي|إجمالي|اجمالي|الإجمالى|الاجمالى|المجموع|مجموع|total|totals|sum|grand\s+total)\s*:?\s*$/i;

/** Cells the model writes when it has nothing: they must not make a column non-numeric. */
const EMPTY_RE = /^\s*(|—|–|-|_|n\/?a|null|none|غير متاح|لا يوجد)\s*$/i;

/** `1.46074e+07` — pandas' default for a wide float column. */
const SCIENTIFIC_RE = /\d[eE][+-]?\d+/;

/** Currency the model still prefixes onto figures. Belongs in the header, not in 30 cells. */
const CURRENCY_RE = /^(LE|EGP|USD|EUR|GBP|SAR|AED|ج\.?\s?م\.?|\$|€|£)\s*/i;

/**
 * How a numeric column's cells are shown.
 *
 * `raw` keeps exactly what the model wrote — the design's default, because the
 * compact form it already emits (`3.15B`) is the one the answer's prose uses.
 * The other two exist because pandas sometimes prints a float column its own
 * way and the result is unreadable in a business answer.
 */
export type DisplayMode = 'raw' | 'compact' | 'integer';

export interface DataColumn {
  header: string;
  /** Set when the model bolded the header — the derived column gets extra weight. */
  emphasised: boolean;
  kind: 'label' | 'number' | 'percent' | 'text';
  /** Lifted out of the cells and shown beside the header instead. */
  currency?: string;
  /** Set from the column's own contents; see DisplayMode. */
  display: DisplayMode;
}

export interface DataTableModel {
  columns: DataColumn[];
  rows: string[][];
  /** The model's own totals line, pulled out of the body so it can be pinned. */
  totals?: string[];
  dir: 'rtl' | 'ltr';
}

const text = (node: any): string => {
  if (!node) return '';
  if (node.type === 'text') return node.value ?? '';
  if (Array.isArray(node.children)) return node.children.map(text).join('');
  return '';
};

const hasStrong = (node: any): boolean => {
  if (!node || typeof node !== 'object') return false;
  if (
    node.type === 'element' &&
    (node.tagName === 'strong' || node.tagName === 'b')
  ) {
    return true;
  }
  return Array.isArray(node.children) && node.children.some(hasStrong);
};

const childrenOf = (node: any, tagName: string): any[] =>
  (node?.children ?? []).filter(
    (c: any) => c?.type === 'element' && c.tagName === tagName
  );

const cellsOf = (tr: any): any[] =>
  (tr?.children ?? []).filter(
    (c: any) =>
      c?.type === 'element' && (c.tagName === 'td' || c.tagName === 'th')
  );

export const isEmptyCell = (value: string): boolean => EMPTY_RE.test(value);

/**
 * Numeric value of a cell, or null when it is not a number.
 *
 * Understands the compact form the model emits (`3.15B`, `502.8M`), thousands
 * separators, percentages and Arabic-Indic digits. Scale suffixes are expanded
 * so that sorting a column of mixed `B` and `M` figures orders by magnitude
 * rather than alphabetically, which is the whole point of sorting it.
 */
export const numericValue = (raw: string): number | null => {
  if (isEmptyCell(raw)) return null;
  let t = raw.replace(CURRENCY_RE, '').trim();
  // Arabic-Indic and extended Arabic-Indic digits to ASCII.
  t = t.replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660));
  t = t.replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06f0));
  t = t.replace(/\u066B/g, '.'); // Arabic decimal separator
  // Thousands separators, NBSP, Arabic thousands separator, bidi marks.
  t = t.replace(/[,\s\u00A0\u066C\u200E\u200F]/g, '');

  // Accounting negatives wrap the whole figure, suffix included: "(1.5M)".
  let negative = false;
  const wrapped = t.match(/^\((.+)\)$/);
  if (wrapped) {
    negative = true;
    t = wrapped[1];
  }

  const m = t.match(/^([-+]?)(\d*\.?\d+(?:e[+-]?\d+)?)\s*([kmb])?\s*%?$/i);
  if (!m) return null;
  let v = parseFloat(m[2]);
  if (!Number.isFinite(v)) return null;
  const suffix = m[3]?.toLowerCase();
  if (suffix === 'k') v *= 1e3;
  else if (suffix === 'm') v *= 1e6;
  else if (suffix === 'b') v *= 1e9;
  if (negative || m[1] === '-') v = -v;
  return v;
};

const isPercent = (raw: string): boolean =>
  !isEmptyCell(raw) && /%\s*$/.test(raw.trim()) && numericValue(raw) !== null;

const currencyOf = (raw: string): string | undefined => {
  const m = raw.trim().match(CURRENCY_RE);
  return m ? m[1].replace(/\s+/g, '') : undefined;
};

export const parseDataTable = (node: any): DataTableModel | null => {
  if (!node) return null;

  const thead = childrenOf(node, 'thead')[0];
  const tbody = childrenOf(node, 'tbody')[0];
  const headerRow = thead ? childrenOf(thead, 'tr')[0] : undefined;
  if (!headerRow) return null;

  const headerCells = cellsOf(headerRow);
  if (!headerCells.length) return null;

  const bodyRows = (tbody ? childrenOf(tbody, 'tr') : []).map((tr: any) =>
    cellsOf(tr).map((c: any) => text(c).trim())
  );
  if (!bodyRows.length) return null;

  const width = headerCells.length;
  // Ragged rows happen when the model miscounts pipes; pad rather than drop, so
  // a malformed table still renders instead of vanishing out of the answer.
  const rows = bodyRows.map((r: string[]) => {
    const padded = r.slice(0, width);
    while (padded.length < width) padded.push('');
    return padded;
  });

  // Pull the model's totals line out of the body so it can be pinned and kept
  // out of sorting — a totals row that sorts into the middle is simply wrong.
  let totals: string[] | undefined;
  const last = rows[rows.length - 1];
  if (rows.length > 1 && last && TOTALS_RE.test(last[0] ?? '')) {
    totals = rows.pop();
  }

  const columns: DataColumn[] = headerCells.map((cell: any, i: number) => {
    const header = text(cell).trim();
    const values = rows.map((r) => r[i] ?? '');
    const filled = values.filter((v) => !isEmptyCell(v));

    let kind: DataColumn['kind'];
    if (filled.length && filled.every(isPercent)) kind = 'percent';
    else if (filled.length && filled.every((v) => numericValue(v) !== null))
      kind = 'number';
    else if (i === 0) kind = 'label';
    else kind = 'text';

    // Lift a currency prefix only when the whole column agrees on it; a mixed
    // column keeps its prefixes, because dropping them would lose information.
    let currency: string | undefined;
    if (kind === 'number' && filled.length) {
      const found = filled.map(currencyOf);
      if (found[0] && found.every((c) => c === found[0])) currency = found[0];
    }

    // A column is reformatted only when the model's own text is unreadable,
    // and then the WHOLE column is, so it stays internally consistent.
    let display: DisplayMode = 'raw';
    if ((kind === 'number' || kind === 'percent') && filled.length) {
      if (filled.some((v) => SCIENTIFIC_RE.test(v))) {
        // `1.46074e+07` is what pandas prints for a wide float column. No one
        // reads a business figure in scientific notation.
        display = 'compact';
      } else if (
        kind === 'number' &&
        filled.some((v) => /\.\d+$/.test(v.trim())) &&
        filled.every((v) => {
          const n = numericValue(v);
          return n !== null && Number.isInteger(n);
        })
      ) {
        // A rank column printed as `1.00`, `2.00` — every value is a whole
        // number, so the decimals carry nothing.
        display = 'integer';
      }
    }

    return { header, emphasised: hasStrong(cell), kind, currency, display };
  });

  // Column 0 is the reading edge — the label the rest of the row describes.
  if (columns[0] && columns[0].kind === 'text') columns[0].kind = 'label';

  const sample = [
    ...columns.map((c) => c.header),
    ...rows.map((r) => r[0] ?? '')
  ].join(' ');

  return {
    columns,
    rows,
    totals,
    dir: ARABIC_RE.test(sample) ? 'rtl' : 'ltr'
  };
};

/**
 * What a cell actually shows: the column currency stripped (it is in the
 * header), and the figure reformatted when the column asked for it.
 */
export const cellDisplay = (value: string, column: DataColumn): string => {
  const stripped = column.currency
    ? value.replace(CURRENCY_RE, '').trim()
    : value;
  if (column.display === 'raw' || isEmptyCell(stripped)) return stripped;

  const n = numericValue(stripped);
  if (n === null) return stripped;

  if (column.display === 'integer') return n.toLocaleString('en-US');
  return compact(n) + (column.kind === 'percent' ? '%' : '');
};

export const tableToTsv = (model: DataTableModel): string => {
  const clean = (s: string) => (s ?? '').replace(/[\t\r\n]+/g, ' ').trim();
  const lines = [model.columns.map((c) => clean(c.header)).join('\t')];
  for (const row of model.rows) lines.push(row.map(clean).join('\t'));
  if (model.totals) lines.push(model.totals.map(clean).join('\t'));
  return lines.join('\n');
};

export const tableToCsv = (model: DataTableModel): string => {
  const cell = (s: string) => `"${(s ?? '').replace(/"/g, '""')}"`;
  const lines = [model.columns.map((c) => cell(c.header)).join(',')];
  for (const row of model.rows) lines.push(row.map(cell).join(','));
  if (model.totals) lines.push(model.totals.map(cell).join(','));
  return lines.join('\r\n');
};
